import { Card, Text, Grid, Box, Stack } from '@mantine/core';
import type { AvailabilityRequestWithDetails } from '../../../services/api/coachPortal';
import { formatDateRange, getModalityLabel } from './helpers';

export interface CohortDetailsCardProps {
  request: AvailabilityRequestWithDetails;
}

export function CohortDetailsCard({ request }: CohortDetailsCardProps) {
  const modalities = request.modalities.map(m => getModalityLabel(m)).join(', ');

  // MED-1025: render session count + duration per modality so a mixed-modality
  // cohort (e.g. 8 × 75 min group + 2 × 45 min 1:1) reads accurately instead of
  // being flattened into one summed count and a single overwritten duration.
  // Only modalities with both values populated are shown as a breakdown line.
  const sessionModalities = request.modality_details.filter(
    m => m.session_count != null && m.session_duration_minutes != null,
  );

  return (
    <Card withBorder padding="lg" radius="md">
      <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb="md">
        Cohort Details
      </Text>
      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <Text size="xs" c="dimmed">
            Partner
          </Text>
          <Text size="sm" fw={500}>
            {request.partner_name || 'N/A'}
          </Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <Text size="xs" c="dimmed">
            Program
          </Text>
          <Text size="sm" fw={500}>
            {request.program_name || request.cohort_name}
          </Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <Text size="xs" c="dimmed">
            Sessions
          </Text>
          {sessionModalities.length > 1 ? (
            <Stack gap={2}>
              {sessionModalities.map(m => (
                <Text key={m.id} size="sm" fw={500}>
                  {m.session_count} × {m.session_duration_minutes} min{' '}
                  {getModalityLabel(m.modality_type)}
                </Text>
              ))}
            </Stack>
          ) : sessionModalities.length === 1 ? (
            <Text size="sm" fw={500}>
              {sessionModalities[0].session_count} × {sessionModalities[0].session_duration_minutes}{' '}
              minutes
            </Text>
          ) : (
            <Text size="sm" fw={500}>
              {request.session_count} × {request.session_duration_minutes} minutes
            </Text>
          )}
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <Text size="xs" c="dimmed">
            Cohort Dates
          </Text>
          <Text size="sm" fw={500}>
            {formatDateRange(request.cohort_start_date, request.cohort_end_date)}
          </Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <Text size="xs" c="dimmed">
            Modalities
          </Text>
          <Text size="sm" fw={500}>
            {modalities || 'N/A'}
          </Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <Text size="xs" c="dimmed">
            Compensation
          </Text>
          <Text size="sm" fw={500}>
            {request.compensation_text || 'N/A'}
          </Text>
        </Grid.Col>
        {(request.cohort_description || request.participant_capacity) && (
          <Grid.Col span={12}>
            <Text size="xs" c="dimmed">
              Participants
            </Text>
            <Text size="sm" fw={500}>
              {request.cohort_description || `${request.participant_capacity} participants`}
            </Text>
          </Grid.Col>
        )}
      </Grid>

      {request.cohort_context && (
        <Box
          mt="md"
          p="md"
          style={{
            background: 'var(--mantine-color-gray-0)',
            borderRadius: 'var(--mantine-radius-md)',
          }}
        >
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb="xs">
            Additional Context from Medley
          </Text>
          <Text size="sm">{request.cohort_context}</Text>
        </Box>
      )}
    </Card>
  );
}
